import {
  BooleanOrNullOrUndefined,
  DateOrNullOrUndefined,
  ForceMap,
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
} from 'src/app/shared/utils';
import { Objective } from './objective.v2.model';
import { PlanFirstTrialStep } from './plan-first-trial-step.v2.model';
import { ChartCommentV2, buildChartCommentV2 } from './chart-comment.v2.model';
import { PLAN_CONST } from './constants';

export type PlanStatus =
  | 'active'
  | 'inactive'
  | 'discontinued';


export const PLAN_STATUS_MAP: Record<PlanStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  discontinued: 'Discontinued',
};

export type PlanCategory =
  | 'maladaptive'
  | 'replacement'
  | 'caregiver_training'
  | 'teacher_training'
  | 'rbt_training';

export const PLAN_CATEGORY_MAP: Record<PlanCategory, string> = {
  maladaptive: 'Maladaptive',
  replacement: 'Replacement',
  caregiver_training: 'Caregiver Training',
  rbt_training: 'RBT Training',
  teacher_training: 'Teacher Training',
};

export type PlanTimeframe = 'per_week' | 'per_hour';

export type PlanUnitType = 'occurrences' | 'seconds' | 'minutes';

export const PLAN_TIMEFRAME_MAP: Record<PlanTimeframe, string> = {
  per_week: 'Per Week',
  per_hour: 'Per Hour',
};

export const PLAN_UNIT_TYPE_MAP: Record<PlanUnitType, string> = {
  occurrences: 'Occurrences',
  seconds: 'Seconds',
  minutes: 'Minutes',
};

export class PlanV2 {
  //internal
  index?: number;
  id!: number;
  bip_id!: number;
  category!: PlanCategory;
  //external
  name!: string;
  status!: PlanStatus;
  description!: string;
  topographical_definition?: string;
  current_progress?: string;
  baseline_level?: number;
  baseline_date?: Date | null;
  added_date?: Date;
  baseline_timeframe!: PlanTimeframe | null;
  initial_intensity?: number;
  current_intensity?: number;
  intensity_key!: string;
  timeframe!: PlanTimeframe | null;
  unit_type!: PlanUnitType | null;
  setting_event_antecedent?: string;
  hypothesized_function?: string;
  //
  objectives!: Objective[];
  chartComments!: ChartCommentV2[];
  //other
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
  is_required?: boolean;
  first_trial?: boolean;
  first_trial_steps?: PlanFirstTrialStep[];
  show_table_objectives_plan?: boolean;
  show_chart_plan?: boolean;

  constructor(data: Partial<PlanV2> = {}) {
    const rawTimeframe = StringOrNullOrUndefined(data.timeframe);
    const rawBaselineTimeframe = StringOrNullOrUndefined((data as Record<string, unknown>)['baseline_timeframe']);
    const chartCommentsSource =
      (data as { chartComments?: unknown; chart_comments?: unknown }).chartComments ??
      (data as Record<string, unknown>)['chart_comments'];
    const chartCommentsRaw = Array.isArray(chartCommentsSource)
      ? (chartCommentsSource as Partial<ChartCommentV2>[])
      : [];
    const rawUnitType = StringOrNullOrUndefined((data as Record<string, unknown>)['unit_type']);
    const self = {
      ...data,
      id: NumberOrNullOrUndefined(data.id) ?? 0,
      bip_id: NumberOrNullOrUndefined(data.bip_id) ?? 0,
      name: StringOrNullOrUndefined(data.name) ?? '',
      description: StringOrNullOrUndefined(data.description) ?? '',
      topographical_definition: StringOrNullOrUndefined(data.topographical_definition),
      current_progress: StringOrNullOrUndefined(data.current_progress),
      baseline_level: NumberOrNullOrUndefined(data.baseline_level),
      // Permitir que baseline_date sea null cuando viene del servidor
      baseline_date: DateOrNullOrUndefined(data.baseline_date) ?? null,
      added_date: DateOrNullOrUndefined((data as Record<string, unknown>)['added_date'] ?? (data as Record<string, unknown>)['Added_date']),
      baseline_timeframe:
        rawBaselineTimeframe === 'per_week' || rawBaselineTimeframe === 'per_hour'
          ? (rawBaselineTimeframe as PlanTimeframe)
          : null,
      initial_intensity: NumberOrNullOrUndefined(data.initial_intensity),
      current_intensity: NumberOrNullOrUndefined(data.current_intensity),
      intensity_key: StringOrNullOrUndefined(data.intensity_key) ?? '',
      timeframe:
        rawTimeframe === 'per_week' || rawTimeframe === 'per_hour'
          ? (rawTimeframe as PlanTimeframe)
          : null,
      unit_type:
        rawUnitType === 'occurrences' || rawUnitType === 'seconds' || rawUnitType === 'minutes'
          ? (rawUnitType as PlanUnitType)
          : null,
      setting_event_antecedent: StringOrNullOrUndefined(data.setting_event_antecedent),
      hypothesized_function: StringOrNullOrUndefined(data.hypothesized_function),
      category: ((StringOrNullOrUndefined(data.category) as PlanCategory | undefined) ?? 'maladaptive'),
      status: ((StringOrNullOrUndefined(data.status) as PlanStatus | undefined) ?? 'active'),
      objectives: ForceMap(data.objectives ?? [], Objective),
      chartComments: (chartCommentsRaw ?? []).map((c) => buildChartCommentV2(c)).filter((c): c is ChartCommentV2 => c !== null),
      created_at: DateOrNullOrUndefined(data.created_at),
      updated_at: DateOrNullOrUndefined(data.updated_at),
      deleted_at: DateOrNullOrUndefined(data.deleted_at),
      is_required: BooleanOrNullOrUndefined(data.is_required),
      first_trial: BooleanOrNullOrUndefined(data.first_trial),
      first_trial_steps: data.first_trial_steps
        ? data.first_trial_steps.map((step) => new PlanFirstTrialStep(step))
        : undefined,
      show_table_objectives_plan: BooleanOrNullOrUndefined(data.show_table_objectives_plan),
      show_chart_plan: BooleanOrNullOrUndefined(data.show_chart_plan),
    };
    //Extra Changes
    self.objectives = self.objectives.map((item, index) => ({
      ...item,
      index,
    }));

    if (self.category === 'maladaptive' && !self.timeframe) {
      self.timeframe = 'per_week';
    }

    if (self.category === 'maladaptive' && !self.baseline_timeframe) {
      self.baseline_timeframe = 'per_hour';
    }

    if (self.category === 'maladaptive') {
      self.unit_type = (self.unit_type ?? 'occurrences') as PlanUnitType;
    } else {
      self.unit_type = null;
    }

    self.chartComments = self.chartComments ?? [];

    Object.assign(this, self);
  }

  static getDefault(): PlanV2 {
    return new PlanV2({
      id: 0,
      bip_id: 0,
      name: '',
      description: '',
      topographical_definition:'',
      baseline_level: 0,
      baseline_date: null,
      added_date: new Date(),
      baseline_timeframe: 'per_hour',
      initial_intensity: 0,
      current_intensity: 0,
      intensity_key: '',
      timeframe: 'per_week',
      unit_type: 'occurrences',
      category: 'maladaptive',
      status: 'active',
      objectives: [],
      chartComments: [],
      first_trial: false,
      first_trial_steps: [],
      index: undefined, // Changed from 0 to undefined
      created_at: undefined,
      updated_at: undefined,
      deleted_at: undefined,
    });
  }
  static getDefaultsCaregiverPlan(): PlanV2[] {
    return [
      {
        id: 0,
        bip_id: 0,
        name: 'Caregiver Training Plan',
        description: PLAN_CONST.CAREGIVER_PLAN,
        baseline_level: undefined,
        baseline_date: null,
        added_date: new Date(),
        baseline_timeframe: null,
        initial_intensity: undefined,
        current_intensity: undefined,
        intensity_key: '',
        timeframe: null,
        unit_type: null,
        category: 'caregiver_training',
        status: 'active',
        objectives: Objective.getDefaultCaregiverObjectives(),
        chartComments: [],
        index: undefined, // Changed from 0 to undefined
        created_at: undefined,
        updated_at: undefined,
        deleted_at: undefined,
      },
    ];
  }
}
